import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { useTableStore } from '@/store/storeTable';

export interface CellSaveItem {
  tableKey: string;
  rowId: number | string;
  columnName: string;
  oldValue: unknown;
  newValue: unknown;
}

type SaveSuccessCallback = () => void | Promise<void>;

export interface CellSave {
  saving: Ref<boolean>;
  saveErrorMessage: Ref<string>;
  hasSaveError: ComputedRef<boolean>;
  saveCell: (item: CellSaveItem, onSaveSuccess?: SaveSuccessCallback | null) => Promise<boolean>;
}

/**
 * Delt lagre-flyt for edit-cellekomponentene (WI 1871).
 *
 * Eier feilfangsten rundt `tableStore.saveData` slik at .vue-komponentene er rene
 * presentasjonslag uten try/catch: komponenten kaller `saveCell` og leser
 * `saving` / `saveErrorMessage` reaktivt. Storen håndterer selve API-feilen
 * (logging + global notifikasjon) og re-thrower; her oversettes den til reaktiv
 * felt-feiltilstand per celle — backend-feil svelges aldri stille.
 *
 * `saveCell` returnerer true når lagringsforsøket fullførte uten feil (uavhengig
 * av om storen faktisk lagret noe), false ved lagringsfeil. `onSaveSuccess`
 * kjøres kun når storen rapporterer at noe ble lagret.
 */
export function useCellSave (): CellSave {
  const tableStore = useTableStore();
  const saving = ref(false);
  const saveErrorMessage = ref('');
  const hasSaveError = computed(() => saveErrorMessage.value !== '');

  async function saveCell (item: CellSaveItem, onSaveSuccess: SaveSuccessCallback | null = null): Promise<boolean> {
    saveErrorMessage.value = '';
    saving.value = true;
    try {
      const didSave = await tableStore.saveData(item);
      if (didSave) await onSaveSuccess?.();
      return true;
    } catch (error: unknown) {
      // Logging her er pinnet av etablerte editComponents-tester (saveData mockes uten storens
      // egen logging); i tillegg settes den reaktive feltfeilen komponentene viser.
      console.error('[useCellSave] Lagring av celle feilet', { item, error });
      saveErrorMessage.value = 'Feil ved lagring';
      return false;
    } finally {
      saving.value = false;
    }
  }

  return { saving, saveErrorMessage, hasSaveError, saveCell };
}
